import { useState, useCallback } from 'react'
import { Upload, X, FileText, CheckCircle, AlertCircle, Music, Box } from 'lucide-react'
import { useDropzone, type FileRejection } from 'react-dropzone'
import { unzip, type Unzipped } from 'fflate'
import { useAuth } from '../../hooks/useAuth'
import { PlySeqReader } from '../../utils/plyseq'
import { SpzWorkerPool } from './spzWorkerPool'
import { prepareSceneAsset, sceneFileExt } from './sceneLayerAsset'
import { uploadSceneAsset } from './sceneLayerUpload'
import { PROXY_PUT_MAX_BYTES, uploadModelMultipart } from './modelMultipartUpload'
import type { FourDStats } from '@/lib/fourdv/scan-4dv'

const SEQUENCE_EXTS = ['.4dms', '.plyseq'] as const
const SINGLE_EXTS = ['.splatv', '.splat', '.ply', '.4dv'] as const
const CLIPGS_EXTS = ['.zip'] as const // ClipGStream dataset zip (manifest.json + base/ + clipNNN/)
const ALL_EXTS = [...SINGLE_EXTS, ...SEQUENCE_EXTS, ...CLIPGS_EXTS]
const AUDIO_EXTS = ['.mp3', '.wav', '.m4a', '.aac', '.ogg'] as const
// Content type for audio bundled inside a ClipGStream zip (fflate hands back raw
// bytes with no File.type, so we derive it from the extension).
const AUDIO_MIME: Record<string, string> = {
  '.mp3': 'audio/mpeg',
  '.wav': 'audio/wav',
  '.m4a': 'audio/mp4',
  '.aac': 'audio/aac',
  '.ogg': 'audio/ogg',
}
const MAX_SIZE_BYTES = 5 * 1024 * 1024 * 1024 // 5 GB (R2 single-PUT ceiling)
const MAX_SIZE_LABEL = '5GB'
const MAX_AUDIO_BYTES = 50 * 1024 * 1024 // 50 MB

// Runtime files kept from a ClipGStream zip (manifest.json, decode_mlps.bin,
// base/ + clipNNN/ assets). Any bundled audio (any format) is handled separately
// and routed to audio.mp3 via the audio endpoint. Everything else — onnx/ debug
// weights, archive junk — is dropped client-side before upload.
const CLIPGS_FILE_EXTS = ['.json', '.bin', '.f16', '.f32'] as const
// Each dataset file is PUT through the Worker proxy, whose request body is
// capped by Cloudflare at ~100MB. Real datasets top out around ~20MB/file.
const CLIPGS_MAX_FILE_BYTES = 95 * 1024 * 1024

function getExt(name: string): string {
  const i = name.lastIndexOf('.')
  return i === -1 ? '' : name.slice(i).toLowerCase()
}

interface UploadProgress {
  status: 'idle' | 'decoding' | 'uploading' | 'processing' | 'completed' | 'error'
  progress: number
  uploadId?: string
  error?: string
  // Sequence (.4dms / .plyseq) extras
  frameCurrent?: number
  frameTotal?: number
  /** Scene conversion and upload progress. */
  sceneStatus?: string
  // Custom label for the progress row (ClipGStream zip flow)
  detail?: string
}

// vertices / duration / metadata are only set when the uploader read them from
// the file itself (a .4dv's stats); duration is exact seconds.
export interface UploadedFileInfo {
  filename: string
  fileSize: number
  vertices?: number
  duration?: number
  metadata?: Record<string, unknown>
}

interface ModelUploaderProps {
  onUploadComplete: (uploadId: string, fileInfo: UploadedFileInfo) => void
}

export default function ModelUploader({ onUploadComplete }: ModelUploaderProps) {
  const { token, adminEmail } = useAuth()
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    status: 'idle',
    progress: 0
  })
  const [file, setFile] = useState<File | null>(null)
  const [compressToSpz, setCompressToSpz] = useState(false)
  const [audioFile, setAudioFile] = useState<File | null>(null)
  // Optional persistent scene 3DGS shown behind the playing sequence.
  const [sceneFile, setSceneFile] = useState<File | null>(null)

  // PUT a soundtrack to models/{uploadId}/audio.mp3 (beside the manifest/original
  // file), where every viewer looks for it. Throws on failure so the caller can
  // surface it.
  const putAudio = async (uploadId: string, body: BlobPart, contentType: string) => {
    const res = await fetch(
      `${import.meta.env.VITE_API_URL}/api/v2/admin/models/proxy/upload/${uploadId}/audio`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Admin-Email': adminEmail!,
          'Content-Type': contentType || 'audio/mpeg',
        },
        body,
      },
    )
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      let detail = text
      try { detail = JSON.parse(text)?.error || text } catch { /* keep raw */ }
      throw new Error(`Failed to upload audio (${res.status}): ${detail || res.statusText}`)
    }
  }

  // Upload the separately-selected soundtrack, if any. No-op otherwise.
  const uploadAudio = async (uploadId: string) => {
    if (!audioFile) return
    await putAudio(uploadId, audioFile, audioFile.type || 'audio/mpeg')
  }

  // Prepare and upload the optional scene, then put its R2 key in the manifest.
  const uploadSceneLayer = async (uploadId: string): Promise<string | null> => {
    if (!sceneFile) return null
    // .spz goes up verbatim; .ply is transcoded to SH degree 0 .spz first.
    const prepared = await prepareSceneAsset(sceneFile, (m) =>
      setUploadProgress((prev) => ({ ...prev, sceneStatus: m })))
    const key = await uploadSceneAsset(prepared, {
      apiBase: import.meta.env.VITE_API_URL,
      uploadId, token, adminEmail,
      onStatus: (m) => setUploadProgress((prev) => ({ ...prev, sceneStatus: m })),
    })
    if (prepared.transcoded) {
      console.log(
        `[sceneLayer] ${(prepared.originalBytes / 1e6).toFixed(2)} MB .ply → ` +
        `${(prepared.uploadBytes / 1e6).toFixed(2)} MB .spz (SH degree 0)`,
      )
    }
    setUploadProgress((prev) => ({ ...prev, sceneStatus: undefined }))
    return key
  }

  const startUpload = async (file: File, fileStats?: Omit<UploadedFileInfo, 'filename' | 'fileSize'>) => {
    try {
      setUploadProgress({ status: 'uploading', progress: 0 })

      // Step 1: Get presigned URL
      const startResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/v2/admin/models/upload/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Admin-Email': adminEmail!
        },
        body: JSON.stringify({
          filename: file.name,
          fileSize: file.size,
          mimeType: file.type || 'application/octet-stream'
        })
      })

      if (!startResponse.ok) {
        throw new Error('Failed to start upload')
      }

      const { data: uploadData } = await startResponse.json()
      const { uploadId, presignedUrl } = uploadData as {
        uploadId: string
        presignedUrl?: string | null
      }

      // Step 2: Upload file. Prefer the presigned R2 URL when available (works
      // for files >100MB which would otherwise be blocked by Cloudflare's
      // Worker body-size cap). Without one, a file too large for a single
      // request goes through the Worker in parts; smaller files use one proxy PUT.
      if (!presignedUrl && file.size > PROXY_PUT_MAX_BYTES) {
        let shown = -1
        const startedAt = performance.now()
        const { partCount } = await uploadModelMultipart(file, {
          apiBase: import.meta.env.VITE_API_URL,
          uploadId, token, adminEmail,
          onProgress: (sent, total) => {
            const progress = Math.round((sent / total) * 100)
            if (progress === shown) return
            shown = progress
            setUploadProgress(prev => ({ ...prev, progress }))
          },
          onStatus: (detail) => setUploadProgress(prev => ({ ...prev, detail })),
        })
        console.log(
          `[upload] ${file.name}: ${(file.size / 1e6).toFixed(1)} MB in ${partCount} part(s), ` +
          `${((performance.now() - startedAt) / 1000).toFixed(1)} s`,
        )
      } else {
        const xhr = new XMLHttpRequest()

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100)
            setUploadProgress(prev => ({ ...prev, progress }))
          }
        })

        await new Promise((resolve, reject) => {
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(xhr.response)
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText || 'no response body'}`))
            }
          }
          xhr.onerror = () => reject(new Error('Upload failed (network or CORS error)'))

          if (presignedUrl) {
            // Direct-to-R2 PUT. The presigned URL bakes in auth — only the
            // signed Content-Type header is allowed on the request.
            xhr.open('PUT', presignedUrl)
            xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
          } else {
            const proxyUrl = `${import.meta.env.VITE_API_URL}/api/v2/admin/models/proxy/upload/${uploadId}`
            xhr.open('PUT', proxyUrl)
            xhr.setRequestHeader('Authorization', `Bearer ${token}`)
            xhr.setRequestHeader('X-Admin-Email', adminEmail!)
            xhr.setRequestHeader('X-Filename', file.name)
            xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
          }
          xhr.send(file)
        })
      }

      setUploadProgress({ status: 'processing', progress: 100, uploadId })

      // Step 2.5: Upload optional soundtrack to models/{uploadId}/audio.mp3
      await uploadAudio(uploadId)

      // Step 3: Complete upload
      const completeResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/v2/admin/models/upload/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Admin-Email': adminEmail!
        },
        body: JSON.stringify({ uploadId })
      })

      if (!completeResponse.ok) {
        throw new Error('Failed to complete upload')
      }

      setUploadProgress({ status: 'completed', progress: 100, uploadId })
      onUploadComplete(uploadId, { filename: file.name, fileSize: file.size, ...fileStats })
      console.log(uploadId, { filename: file.name, fileSize: file.size, ...fileStats })
    } catch (error) {
      console.error('Upload error:', error)
      setUploadProgress({
        status: 'error',
        progress: 0,
        error: error instanceof Error ? error.message : 'Upload failed'
      })
    }
  }

  // .4dv: read the file's stats in the browser BEFORE anything is uploaded, so
  // a broken or non-4D file is rejected here rather than stored. The stats
  // pre-fill the model form and are kept as metadata.fourdv.
  const startFourDVUpload = async (file: File) => {
    let stats: FourDStats
    try {
      setUploadProgress({ status: 'decoding', progress: 0, detail: 'Checking .4dv file...' })
      // Read in slices: a long clip is hundreds of MB and is never held whole.
      const { scan4dvFile } = await import('@/lib/fourdv/scan-4dv')
      try {
        stats = await scan4dvFile(file)
      } catch (e) {
        throw new Error(
          `"${file.name}" is not a valid .4dv file: ${e instanceof Error ? e.message : String(e)}. Nothing was uploaded.`,
        )
      }
    } catch (error) {
      console.error('.4dv check error:', error)
      setUploadProgress({
        status: 'error',
        progress: 0,
        error: error instanceof Error ? error.message : 'Could not read the .4dv file',
      })
      return
    }
    await startUpload(file, {
      vertices: stats.gaussianCount,
      duration: stats.durationSec,
      metadata: { fourdv: stats },
    })
  }

  const startSequenceUpload = async (file: File, asSpz: boolean) => {
    const apiUrl = import.meta.env.VITE_API_URL
    try {
      // 1. Decode the .4dms client-side
      setUploadProgress({ status: 'decoding', progress: 0 })
      const reader = await PlySeqReader.open(file)
      const frameIds = Array.from(reader.frameIds())
      const total = frameIds.length

      if (total === 0) throw new Error('Sequence contains no frames')

      // .ply → .spz transcoding runs in a pool of Web Workers (Spark's wasm is
      // loaded once per worker), so frames convert in parallel and off the UI
      // thread — much faster than the old per-frame, main-thread loop.
      const hw = (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 4
      const poolSize = asSpz ? Math.min(6, Math.max(2, hw - 1)) : 0
      const spzPool = poolSize ? new SpzWorkerPool(poolSize) : null

      // 2. Open an upload session (uses the original .4dms filename)
      const startResponse = await fetch(`${apiUrl}/api/v2/admin/models/upload/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Admin-Email': adminEmail!,
        },
        body: JSON.stringify({
          filename: file.name,
          fileSize: file.size,
          mimeType: file.type || 'application/octet-stream',
        }),
      })

      if (!startResponse.ok) {
        const body = await startResponse.text().catch(() => '')
        let detail = body
        try { detail = JSON.parse(body)?.error || body } catch { /* keep raw */ }
        throw new Error(`Failed to start upload (${startResponse.status}): ${detail || startResponse.statusText}`)
      }
      const { data: uploadData } = await startResponse.json()
      const { uploadId } = uploadData

      // 3. Decode each frame and PUT it as <uploadId>/frames/frame_<id>.ply
      setUploadProgress({
        status: 'uploading',
        progress: 0,
        uploadId,
        frameCurrent: 0,
        frameTotal: total,
      })

      const MAX_FRAME_ATTEMPTS = 4
      const frameQuery = asSpz ? '?format=spz' : ''

      // PUT one frame to R2 (with retry/backoff). Throws if all attempts fail.
      const uploadFrame = async (fid: number, frameBytes: Uint8Array) => {
        const body = new Blob([frameBytes as BlobPart], { type: 'application/octet-stream' })
        let lastErr = ''
        let lastStatus = 0
        for (let attempt = 1; attempt <= MAX_FRAME_ATTEMPTS; attempt++) {
          try {
            const res = await fetch(
              `${apiUrl}/api/v2/admin/models/proxy/upload/${uploadId}/frame/${fid}${frameQuery}`,
              {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'X-Admin-Email': adminEmail!,
                  'Content-Type': 'application/octet-stream',
                },
                body,
              },
            )
            if (res.ok) return
            lastStatus = res.status
            const text = await res.text().catch(() => '')
            try { lastErr = JSON.parse(text)?.error || text } catch { lastErr = text }
            // Retry on 5xx and 429; bail on other 4xx.
            if (res.status < 500 && res.status !== 429) break
          } catch (e) {
            lastErr = e instanceof Error ? e.message : 'network error'
          }
          if (attempt < MAX_FRAME_ATTEMPTS) {
            const backoff = 400 * Math.pow(2, attempt - 1) // 400, 800, 1600 ms
            await new Promise((r) => setTimeout(r, backoff))
          }
        }
        throw new Error(
          `Frame ${fid} upload failed after ${MAX_FRAME_ATTEMPTS} attempts` +
          (lastStatus ? ` (last status ${lastStatus})` : '') +
          (lastErr ? `: ${lastErr}` : ''),
        )
      }

      // Pipeline: decode sequentially (the reader is stateful) but run
      // transcode (worker pool) + upload concurrently, capped at LIMIT in-flight
      // so transcodes of later frames overlap uploads of earlier ones.
      const LIMIT = asSpz ? Math.min(poolSize + 3, 10) : 6
      let completed = 0
      let firstError: unknown = null
      const inflight = new Set<Promise<void>>()
      try {
        for (let i = 0; i < total && !firstError; i++) {
          const fid = frameIds[i]
          // .slice() so this frame owns its buffer (the pool transfers it, and
          // the next decode won't clobber it).
          const plyBytes = (await reader.exportFramePly(fid)).slice()
          const task = (async () => {
            const frameBytes = spzPool ? await spzPool.transcode(plyBytes) : plyBytes
            await uploadFrame(fid, frameBytes)
            completed++
            setUploadProgress({
              status: 'uploading',
              progress: Math.round((completed / total) * 100),
              uploadId,
              frameCurrent: completed,
              frameTotal: total,
            })
          })().catch((e) => { if (!firstError) firstError = e })
          inflight.add(task)
          void task.finally(() => inflight.delete(task))
          if (inflight.size >= LIMIT) await Promise.race(inflight)
        }
        await Promise.all(inflight)
      } finally {
        spzPool?.dispose()
      }
      if (firstError) throw firstError

      // 4. Upload manifest describing the sequence
      const info = reader.info()
      const staticPath = await uploadSceneLayer(uploadId)

      const manifest = {
        version: 1,
        sourceFilename: file.name,
        sourceFormat: 'plyseq-v2',
        frameFormat: asSpz ? 'spz' : 'ply',
        ...(staticPath ? { staticPath } : {}),
        fps: info.fps,
        frameStart: reader.frameStart,
        frameEnd: reader.frameEnd,
        frameCount: info.frames.count,
        frameIds,
        properties: info.properties,
        perVertexBytes: info.perVertexBytes,
        runs: info.runs,
      }

      const manifestRes = await fetch(
        `${apiUrl}/api/v2/admin/models/proxy/upload/${uploadId}/manifest`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-Admin-Email': adminEmail!,
          },
          body: JSON.stringify(manifest),
        },
      )
      if (!manifestRes.ok) {
        const body = await manifestRes.text().catch(() => '')
        let detail = body
        try { detail = JSON.parse(body)?.error || body } catch { /* keep raw */ }
        throw new Error(`Failed to upload manifest (${manifestRes.status}): ${detail || manifestRes.statusText}`)
      }

      // 4.5. Upload optional soundtrack to models/{uploadId}/audio.mp3
      await uploadAudio(uploadId)

      // 5. Mark the upload session complete
      setUploadProgress((prev) => ({ ...prev, status: 'processing', progress: 100, uploadId }))

      const completeResponse = await fetch(`${apiUrl}/api/v2/admin/models/upload/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Admin-Email': adminEmail!,
        },
        body: JSON.stringify({ uploadId }),
      })

      if (!completeResponse.ok) {
        const body = await completeResponse.text().catch(() => '')
        let detail = body
        try { detail = JSON.parse(body)?.error || body } catch { /* keep raw */ }
        throw new Error(`Failed to complete upload (${completeResponse.status}): ${detail || completeResponse.statusText}`)
      }

      setUploadProgress({
        status: 'completed',
        progress: 100,
        uploadId,
        frameCurrent: total,
        frameTotal: total,
      })
      onUploadComplete(uploadId, { filename: file.name, fileSize: file.size })
    } catch (error) {
      console.error('Sequence upload error:', error)
      setUploadProgress({
        status: 'error',
        progress: 0,
        error: error instanceof Error ? error.message : 'Sequence upload failed',
      })
    }
  }

  // ClipGStream dataset zip: extract client-side, validate the manifest, then
  // PUT every runtime file to models/{uploadId}/<relative path> so the viewer
  // can fetch them exactly like it fetches a dataset folder (manifest.json,
  // base/anchor.f32, clipNNN/hashgrid.f16, decode_mlps.bin, audio.mp3, …).
  const startClipGStreamUpload = async (file: File) => {
    const apiUrl = import.meta.env.VITE_API_URL
    try {
      // 1. Extract the zip in the browser
      setUploadProgress({ status: 'decoding', progress: 0, detail: 'Extracting zip...' })
      const zipBytes = new Uint8Array(await file.arrayBuffer())
      const entries = await new Promise<Unzipped>((resolve, reject) =>
        unzip(zipBytes, (err, out) => (err ? reject(err) : resolve(out))),
      )

      // 2. Locate the dataset root — the shallowest manifest.json (datasets are
      // often zipped inside a top-level folder).
      let rootPrefix: string | null = null
      for (const name of Object.keys(entries)) {
        if (name.endsWith('/') || name.includes('__MACOSX')) continue
        if (name === 'manifest.json' || name.endsWith('/manifest.json')) {
          const prefix = name.slice(0, name.length - 'manifest.json'.length)
          if (rootPrefix === null || prefix.length < rootPrefix.length) rootPrefix = prefix
        }
      }
      if (rootPrefix === null) {
        throw new Error('No manifest.json found in zip — not a ClipGStream dataset')
      }

      let manifest: { clip_size?: number; clips?: unknown[] }
      try {
        manifest = JSON.parse(new TextDecoder().decode(entries[`${rootPrefix}manifest.json`]))
      } catch {
        throw new Error('manifest.json in zip is not valid JSON')
      }
      if (!Array.isArray(manifest.clips) || !manifest.clip_size) {
        throw new Error('manifest.json is missing "clips" / "clip_size" — not a ClipGStream dataset')
      }

      // 3. Collect runtime files, paths relative to the dataset root. Bundled
      //    audio (any format) is pulled aside and later routed to audio.mp3.
      const files: { path: string; bytes: Uint8Array }[] = []
      let zipAudio: { bytes: Uint8Array; contentType: string } | null = null
      for (const [name, bytes] of Object.entries(entries)) {
        if (name.endsWith('/') || !name.startsWith(rootPrefix)) continue
        const path = name.slice(rootPrefix.length)
        if (!path || path.includes('__MACOSX')) continue
        const segments = path.split('/')
        // Dataset layout is flat or one level deep (base/, clipNNN/)
        if (segments.length > 2 || segments.some((s) => s.startsWith('.'))) continue
        if (segments[0] === 'onnx') continue // debug weights, not used by the viewer

        const ext = getExt(path)
        // Soundtrack bundled in the zip: set it aside and upload via the audio
        // endpoint so it lands at audio.mp3 with the right Content-Type. Prefer a
        // top-level file literally named audio.* ; otherwise take the first found.
        if ((AUDIO_EXTS as readonly string[]).includes(ext)) {
          if (bytes.length > MAX_AUDIO_BYTES) {
            throw new Error(
              `Bundled audio "${path}" is ${(bytes.length / (1024 * 1024)).toFixed(1)} MB; limit is ${(MAX_AUDIO_BYTES / (1024 * 1024)).toFixed(0)} MB`,
            )
          }
          const preferred = /^audio\.[^/.]+$/i.test(path)
          if (!zipAudio || preferred) zipAudio = { bytes, contentType: AUDIO_MIME[ext] || 'audio/mpeg' }
          continue
        }

        if (!CLIPGS_FILE_EXTS.includes(ext as (typeof CLIPGS_FILE_EXTS)[number])) continue
        if (bytes.length > CLIPGS_MAX_FILE_BYTES) {
          throw new Error(
            `"${path}" is ${(bytes.length / (1024 * 1024)).toFixed(0)} MB; per-file limit is ${(CLIPGS_MAX_FILE_BYTES / (1024 * 1024)).toFixed(0)} MB`,
          )
        }
        files.push({ path, bytes })
      }
      if (!files.some((f) => f.path === 'decode_mlps.bin')) {
        throw new Error('zip is missing decode_mlps.bin')
      }

      // 4. Open an upload session (uses the original zip filename)
      const startResponse = await fetch(`${apiUrl}/api/v2/admin/models/upload/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Admin-Email': adminEmail!,
        },
        body: JSON.stringify({
          filename: file.name,
          fileSize: file.size,
          mimeType: file.type || 'application/zip',
        }),
      })
      if (!startResponse.ok) {
        const body = await startResponse.text().catch(() => '')
        let detail = body
        try { detail = JSON.parse(body)?.error || body } catch { /* keep raw */ }
        throw new Error(`Failed to start upload (${startResponse.status}): ${detail || startResponse.statusText}`)
      }
      const { data: uploadData } = await startResponse.json()
      const { uploadId } = uploadData

      // 5. PUT each file (with retry/backoff), progress weighted by bytes
      const totalBytes = files.reduce((sum, f) => sum + f.bytes.length, 0)
      const total = files.length
      let uploadedBytes = 0
      let completed = 0
      setUploadProgress({
        status: 'uploading',
        progress: 0,
        uploadId,
        detail: `Uploading dataset file 0/${total}...`,
      })

      const MAX_ATTEMPTS = 4
      const uploadDatasetFile = async (path: string, bytes: Uint8Array) => {
        const body = new Blob([bytes as BlobPart], { type: 'application/octet-stream' })
        const encodedPath = path.split('/').map(encodeURIComponent).join('/')
        let lastErr = ''
        let lastStatus = 0
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
          try {
            const res = await fetch(
              `${apiUrl}/api/v2/admin/models/proxy/upload/${uploadId}/file/${encodedPath}`,
              {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'X-Admin-Email': adminEmail!,
                  'Content-Type': path.endsWith('.json') ? 'application/json' : 'application/octet-stream',
                },
                body,
              },
            )
            if (res.ok) return
            lastStatus = res.status
            const text = await res.text().catch(() => '')
            try { lastErr = JSON.parse(text)?.error || text } catch { lastErr = text }
            // Retry on 5xx and 429; bail on other 4xx.
            if (res.status < 500 && res.status !== 429) break
          } catch (e) {
            lastErr = e instanceof Error ? e.message : 'network error'
          }
          if (attempt < MAX_ATTEMPTS) {
            const backoff = 400 * Math.pow(2, attempt - 1) // 400, 800, 1600 ms
            await new Promise((r) => setTimeout(r, backoff))
          }
        }
        throw new Error(
          `"${path}" upload failed after ${MAX_ATTEMPTS} attempts` +
          (lastStatus ? ` (last status ${lastStatus})` : '') +
          (lastErr ? `: ${lastErr}` : ''),
        )
      }

      const LIMIT = 6
      let firstError: unknown = null
      const inflight = new Set<Promise<void>>()
      for (let i = 0; i < total && !firstError; i++) {
        const { path, bytes } = files[i]
        const task = (async () => {
          await uploadDatasetFile(path, bytes)
          completed++
          uploadedBytes += bytes.length
          setUploadProgress({
            status: 'uploading',
            progress: totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 100,
            uploadId,
            detail: `Uploading dataset file ${completed}/${total}...`,
          })
        })().catch((e) => { if (!firstError) firstError = e })
        inflight.add(task)
        void task.finally(() => inflight.delete(task))
        if (inflight.size >= LIMIT) await Promise.race(inflight)
      }
      await Promise.all(inflight)
      if (firstError) throw firstError

      // 6. Soundtrack: an explicitly-selected file wins; otherwise use the audio
      //    bundled inside the zip. Both land at models/{uploadId}/audio.mp3.
      if (audioFile) {
        await uploadAudio(uploadId)
      } else if (zipAudio) {
        await putAudio(uploadId, new Blob([zipAudio.bytes as BlobPart]), zipAudio.contentType)
      }

      // 7. Mark the upload session complete
      setUploadProgress((prev) => ({ ...prev, status: 'processing', progress: 100, uploadId }))
      const completeResponse = await fetch(`${apiUrl}/api/v2/admin/models/upload/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Admin-Email': adminEmail!,
        },
        body: JSON.stringify({ uploadId }),
      })
      if (!completeResponse.ok) {
        const body = await completeResponse.text().catch(() => '')
        let detail = body
        try { detail = JSON.parse(body)?.error || body } catch { /* keep raw */ }
        throw new Error(`Failed to complete upload (${completeResponse.status}): ${detail || completeResponse.statusText}`)
      }

      setUploadProgress({ status: 'completed', progress: 100, uploadId })
      onUploadComplete(uploadId, { filename: file.name, fileSize: file.size })
    } catch (error) {
      console.error('ClipGStream upload error:', error)
      setUploadProgress({
        status: 'error',
        progress: 0,
        error: error instanceof Error ? error.message : 'ClipGStream upload failed',
      })
    }
  }

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (!file) return
    const ext = getExt(file.name)
    if (!ALL_EXTS.includes(ext as (typeof ALL_EXTS)[number])) {
      setFile(file)
      setUploadProgress({
        status: 'error',
        progress: 0,
        error: `Unsupported file type "${ext || file.name}". Supported: ${ALL_EXTS.join(', ')}`,
      })
      return
    }
    setFile(file)
    if ((SEQUENCE_EXTS as readonly string[]).includes(ext)) {
      startSequenceUpload(file, compressToSpz)
    } else if ((CLIPGS_EXTS as readonly string[]).includes(ext)) {
      startClipGStreamUpload(file)
    } else if (ext === '.4dv') {
      startFourDVUpload(file)
    } else {
      startUpload(file)
    }
    // audioFile is read (via uploadAudio) inside the upload helpers this closure
    // calls, so it must be a dependency to avoid uploading a stale selection.
  }, [compressToSpz, audioFile, sceneFile])

  const onDropRejected = useCallback((rejections: FileRejection[]) => {
    const r = rejections[0]
    if (!r) return
    setFile(r.file)
    const messages = r.errors.map((e) => {
      if (e.code === 'file-too-large') {
        return `File is ${(r.file.size / (1024 * 1024 * 1024)).toFixed(2)} GB; limit is ${MAX_SIZE_LABEL}`
      }
      if (e.code === 'file-invalid-type') {
        return `Unsupported file type "${getExt(r.file.name) || r.file.name}". Supported: ${ALL_EXTS.join(', ')}`
      }
      if (e.code === 'too-many-files') return 'Please drop one file at a time'
      return e.message
    })
    setUploadProgress({
      status: 'error',
      progress: 0,
      error: messages.join('; '),
    })
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    // No `accept` filter: .4dms / .plyseq files often have an empty MIME type
    // and react-dropzone's MIME-keyed accept can silently reject them. We
    // validate by extension manually inside onDrop instead.
    maxFiles: 1,
    maxSize: MAX_SIZE_BYTES,
    disabled:
      uploadProgress.status === 'decoding' ||
      uploadProgress.status === 'uploading' ||
      uploadProgress.status === 'processing',
  })

  const onDropAudio = useCallback((acceptedFiles: File[]) => {
    const audio = acceptedFiles[0]
    if (!audio) return
    const ext = getExt(audio.name)
    if (!AUDIO_EXTS.includes(ext as (typeof AUDIO_EXTS)[number])) {
      setUploadProgress({
        status: 'error',
        progress: 0,
        error: `Unsupported audio type "${ext || audio.name}". Supported: ${AUDIO_EXTS.join(', ')}`,
      })
      return
    }
    if (audio.size > MAX_AUDIO_BYTES) {
      setUploadProgress({
        status: 'error',
        progress: 0,
        error: `Audio is ${(audio.size / (1024 * 1024)).toFixed(1)} MB; limit is ${(MAX_AUDIO_BYTES / (1024 * 1024)).toFixed(0)} MB`,
      })
      return
    }
    setAudioFile(audio)
  }, [])

  const {
    getRootProps: getAudioRootProps,
    getInputProps: getAudioInputProps,
    isDragActive: isAudioDragActive,
  } = useDropzone({
    onDrop: onDropAudio,
    maxFiles: 1,
    maxSize: MAX_AUDIO_BYTES,
  })

  const reset = () => {
    setFile(null)
    setAudioFile(null)
    setSceneFile(null)
    setUploadProgress({ status: 'idle', progress: 0 })
  }

  return (
    <div className="w-full">
      {uploadProgress.status === 'idle' && (
        <>
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragActive ? 'border-indigo-600 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <input {...getInputProps()} />
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-700 font-medium mb-2">
              {isDragActive ? 'Drop the file here' : 'Drag & drop your model file here'}
            </p>
            <p className="text-sm text-gray-500">or click to select a file</p>
            <p className="text-xs text-gray-400 mt-2">
              Supported formats: .splatv, .splat, .ply, .4dv, .4dms, .zip (max {MAX_SIZE_LABEL}).
              <br />
              <span className="text-gray-500">.4dv (4D Gaussian Splatting) files are checked in the browser before upload; vertex count and duration are read from the file.</span>
              <br />
              <span className="text-gray-500">.4dms files are decoded into per-frame .ply on upload.</span>
              <br />
              <span className="text-gray-500">.zip = ClipGStream dataset (manifest.json + base/ + clipNNN/), extracted and uploaded file-by-file. A bundled audio file, if present, becomes the soundtrack.</span>
            </p>
          </div>
          <label
            className="mt-3 inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={compressToSpz}
              onChange={(e) => setCompressToSpz(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span>
              Compress each frame to <code className="bg-gray-100 px-1 rounded">.spz</code> on upload
              <span className="text-gray-400"> (sequence only — slower, much smaller)</span>
            </span>
          </label>

          {/* Optional soundtrack — stored beside the model as audio.mp3 */}
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Audio File</p>
            {audioFile ? (
              <div className="flex items-center justify-between border rounded-lg p-3">
                <div className="flex items-center min-w-0">
                  <Music className="w-6 h-6 text-gray-400 mr-3 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{audioFile.name}</p>
                    <p className="text-sm text-gray-500">{(audioFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setAudioFile(null)}
                  className="text-gray-400 hover:text-gray-600 flex-shrink-0 ml-3"
                  aria-label="Remove audio file"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div
                {...getAudioRootProps()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  isAudioDragActive ? 'border-indigo-600 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input {...getAudioInputProps()} />
                <Music className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-700 font-medium mb-1">
                  {isAudioDragActive ? 'Drop the audio file here' : 'Drag & drop your audio file here'}
                </p>
                <p className="text-sm text-gray-500">or click to select a file</p>
                <p className="text-xs text-gray-400 mt-2">
                  Optional soundtrack ({AUDIO_EXTS.join(', ')}, max {(MAX_AUDIO_BYTES / (1024 * 1024)).toFixed(0)}MB).
                  <br />
                  <span className="text-gray-500">Stored beside the model and played on loop in the viewer.</span>
                </p>
              </div>
            )}
          </div>

          {/* Optional persistent scene 3DGS — rendered behind the sequence and
              never swapped per frame. Its pose is aligned later in the model
              editor; the default is identity, which is already correct when the
              scene was solved from the same COLMAP sparse/0 as the frames. */}
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Scene Layer (optional)</p>
            {sceneFile ? (
              <div className="flex items-center justify-between border rounded-lg p-3">
                <div className="flex items-center min-w-0">
                  <Box className="w-6 h-6 text-gray-400 mr-3 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{sceneFile.name}</p>
                    <p className="text-sm text-gray-500">
                      {(sceneFile.size / (1024 * 1024)).toFixed(2)} MB
                      {sceneFileExt(sceneFile.name) === 'ply' && (
                        <span className="text-gray-400"> · will be converted to .spz (SH degree 0)</span>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSceneFile(null)}
                  className="text-gray-400 hover:text-gray-600 flex-shrink-0 ml-3"
                  aria-label="Remove scene file"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <label className="block border-2 border-dashed border-gray-300 hover:border-gray-400 rounded-lg p-6 text-center cursor-pointer transition-colors">
                <input
                  type="file"
                  accept=".spz,.ply"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (!f) return
                    if (!sceneFileExt(f.name)) {
                      alert('Scene layer must be a .spz or .ply file')
                      return
                    }
                    setSceneFile(f)
                  }}
                />
                <Box className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-700 font-medium mb-1">Select a scene 3DGS</p>
                <p className="text-sm text-gray-500">.spz or .ply</p>
                <p className="text-xs text-gray-400 mt-2">
                  Stays in the scene while the sequence plays.
                  <br />
                  <span className="text-gray-500">
                    A .ply is converted to SH degree 0 .spz before upload; a .spz is
                    stored exactly as given.
                  </span>
                </p>
              </label>
            )}
          </div>
        </>
      )}

      {file && uploadProgress.status !== 'idle' && (
        <div className="border rounded-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start">
              <FileText className="w-8 h-8 text-gray-400 mr-3 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">{file.name}</p>
                <p className="text-sm text-gray-500">
                  {(file.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>
            </div>
            {uploadProgress.status === 'error' && (
              <button onClick={reset} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* The scene picker (like the .spz option) is only shown before a
              file is chosen and only applies to sequences. If a scene was
              picked and then a .4dv / single file / zip dropped, say that it
              is not uploaded instead of dropping it silently. */}
          {sceneFile && !(SEQUENCE_EXTS as readonly string[]).includes(getExt(file.name)) && (
            <p className="mb-4 text-sm text-amber-700">
              Scene layer "{sceneFile.name}" is not uploaded: it is only used with .4dms / .plyseq sequences.
            </p>
          )}

          {/* Progress bar */}
          {(uploadProgress.status === 'decoding' ||
            uploadProgress.status === 'uploading' ||
            uploadProgress.status === 'processing') && (
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-600">
                  {uploadProgress.status === 'decoding' && (uploadProgress.detail || 'Decoding sequence...')}
                  {uploadProgress.status === 'uploading' && (
                    uploadProgress.detail || (
                      uploadProgress.frameTotal
                        ? `Uploading frame ${uploadProgress.frameCurrent}/${uploadProgress.frameTotal}...`
                        : 'Uploading...'
                    )
                  )}
                  {uploadProgress.status === 'processing' && 'Processing...'}
                </span>
                <span className="text-gray-900 font-medium">{uploadProgress.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress.progress}%` }}
                />
              </div>
              {uploadProgress.sceneStatus && (
                <p role="status" className="mt-2 text-sm text-indigo-600">{uploadProgress.sceneStatus}</p>
              )}
            </div>
          )}

          {/* Status messages */}
          {uploadProgress.status === 'completed' && (
            <div className="flex items-center text-green-600">
              <CheckCircle className="w-5 h-5 mr-2" />
              <span className="font-medium">Upload completed successfully!</span>
            </div>
          )}

          {uploadProgress.status === 'error' && (
            <div className="flex items-start text-red-600">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Upload failed</p>
                <p className="text-sm mt-1">{uploadProgress.error}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
